import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

type FilterDefaults<TFilterKey extends string> = Record<TFilterKey, string>;

type ListQueryStateOptions<TFilterKey extends string> = {
  filters: FilterDefaults<TFilterKey>;
  defaultPageSize?: 25 | 50 | 100;
  debounceMs?: number;
};

const PAGE_SIZES = new Set([25, 50, 100]);

function sortingFromParam(value: string | null): SortingState {
  if (!value) return [];
  return [
    {
      id: value.startsWith("-") ? value.slice(1) : value,
      desc: value.startsWith("-"),
    },
  ].filter((item) => item.id);
}

function sortingToParam(sorting: SortingState): string {
  const first = sorting[0];
  if (!first) return "";
  return `${first.desc ? "-" : ""}${first.id}`;
}

export function useListQueryState<TFilterKey extends string>({
  filters: filterDefaults,
  defaultPageSize = 25,
  debounceMs = 320,
}: ListQueryStateOptions<TFilterKey>) {
  // Filter definitions are configuration, not render state. Several list pages
  // pass an object literal here; retaining that changing object made every
  // callback/effect in this hook change identity on each render and could leave
  // a pending URL update while the user navigated to another route.
  const [stableFilterDefaults] = useState(() => filterDefaults);
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const [searchDraft, setSearchDraft] = useState({ value: search, base: search });
  const searchInput =
    searchDraft.base === search ? searchDraft.value : search;
  const rawPage = Number(params.get("page") ?? 1);
  const rawPageSize = Number(params.get("page_size") ?? defaultPageSize);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 1;
  const pageSize = PAGE_SIZES.has(rawPageSize) ? rawPageSize : defaultPageSize;
  const sorting = useMemo(
    () => sortingFromParam(params.get("sort")),
    [params],
  );
  const filterKeys = useMemo(
    () => Object.keys(stableFilterDefaults) as TFilterKey[],
    [stableFilterDefaults],
  );
  const filters = useMemo(
    () =>
      Object.fromEntries(
        filterKeys.map((key) => [
          key,
          params.get(key) ?? stableFilterDefaults[key],
        ]),
      ) as FilterDefaults<TFilterKey>,
    [stableFilterDefaults, filterKeys, params],
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = false) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(updates)) {
            if (!value) next.delete(key);
            else next.set(key, value);
          }
          if (resetPage) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setSearchInput = useCallback(
    (value: string) => setSearchDraft({ value, base: search }),
    [search],
  );

  useEffect(() => {
    if (searchInput === search) return;
    const timer = window.setTimeout(
      () => updateParams({ search: searchInput.trim() || null }, true),
      debounceMs,
    );
    return () => window.clearTimeout(timer);
  }, [debounceMs, search, searchInput, updateParams]);

  const setFilter = useCallback(
    (key: TFilterKey, value: string) => {
      const defaultValue = stableFilterDefaults[key];
      updateParams({ [key]: value === defaultValue ? null : value }, true);
    },
    [stableFilterDefaults, updateParams],
  );

  const setPage = useCallback(
    (value: number) =>
      updateParams({ page: value > 1 ? String(Math.floor(value)) : null }),
    [updateParams],
  );

  const setPageSize = useCallback(
    (value: number) =>
      updateParams(
        {
          page_size:
            value === defaultPageSize ? null : String(Math.floor(value)),
        },
        true,
      ),
    [defaultPageSize, updateParams],
  );

  const setSorting: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const next =
        typeof updater === "function" ? updater(sorting) : updater;
      updateParams({ sort: sortingToParam(next) || null }, true);
    },
    [sorting, updateParams],
  );

  const clear = useCallback(() => {
    const updates: Record<string, null> = {
      search: null,
      sort: null,
      page: null,
    };
    for (const key of filterKeys) updates[key] = null;
    setSearchInput("");
    updateParams(updates);
  }, [filterKeys, setSearchInput, updateParams]);

  const applyConfiguration = useCallback(
    (configuration: Record<string, string>) => {
      const next = new URLSearchParams();
      const configuredSearch = configuration.search?.trim() ?? "";
      if (configuredSearch) next.set("search", configuredSearch);
      for (const key of filterKeys) {
        const value = configuration[key] ?? stableFilterDefaults[key];
        if (value && value !== stableFilterDefaults[key]) next.set(key, value);
      }
      const configuredPageSize = Number(configuration.page_size);
      if (
        PAGE_SIZES.has(configuredPageSize) &&
        configuredPageSize !== defaultPageSize
      ) {
        next.set("page_size", String(configuredPageSize));
      }
      if (configuration.sort) next.set("sort", configuration.sort);
      setSearchInput(configuredSearch);
      setParams(next, { replace: true });
    },
    [defaultPageSize, stableFilterDefaults, filterKeys, setParams, setSearchInput],
  );

  const configuration = useMemo(
    () => ({
      search,
      ...filters,
      page_size: String(pageSize),
      sort: sortingToParam(sorting),
    }),
    [filters, pageSize, search, sorting],
  );

  const activeFilterCount =
    Number(Boolean(search)) +
    filterKeys.filter((key) => filters[key] !== stableFilterDefaults[key]).length +
    Number(sorting.length > 0);

  return {
    search,
    searchInput,
    setSearchInput,
    filters,
    setFilter,
    page,
    setPage,
    pageSize,
    setPageSize,
    sorting,
    setSorting,
    clear,
    applyConfiguration,
    configuration,
    activeFilterCount,
  };
}
